import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@hcs/design-system';
import { IconButton } from '@hcs/design-system';
import { TextButton } from '@hcs/design-system';
import { Input } from '@hcs/design-system';
import { Dialog } from '@hcs/design-system';
import { MenuItem } from '@hcs/design-system';
import { TwoColumnTable } from '@hcs/design-system';
import { TagIcon } from '@hcs/design-system';
import { DeleteIcon } from '@hcs/design-system';
import { useActiveState } from '@hcs/hooks';
import { usePatchReportPreferencesForOrg } from '@hcs/huell';
import { useReportPreferencesForOrg } from '@hcs/huell';

import { DEFAULT_TAGS } from '../../features/CompTags';

import styles from './OrgReportPreferences.module.css';

const dataHcName = 'comp-tags-options-control';
export const CompTagsOptionsControl = () => {
  const { active, handleClose, handleOpen } = useActiveState();
  const reportOrgPreferencesQuery = useReportPreferencesForOrg();
  const { mutate: patchReportPreferences } = usePatchReportPreferencesForOrg();
  const savedOptions = reportOrgPreferencesQuery?.data?.preferences?.tagOptions;
  const [tagOptions, setTagOptions] = useState<string[]>(savedOptions || []);
  const [newTag, setNewTag] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (savedOptions) {
      setTagOptions(savedOptions);
    }
  }, [savedOptions]);
  const handleAddTag = () => {
    setTagOptions([...tagOptions, newTag]);
    setNewTag('');
    inputRef.current?.focus();
  };
  const hasCustomOptions = !!tagOptions?.length;
  return (
    <>
      <MenuItem
        dataHcName={dataHcName}
        Icon={TagIcon}
        label="Comp Tags Options"
        description="The tags that can be applied to selected comparables in the PEXP application."
        contentRight={
          <div className={styles.Switch}>
            <span className={styles.SelectedValue}>
              {hasCustomOptions ? 'Custom Tags' : 'System Defaults'}
            </span>
            <TextButton
              dataHcName={`${dataHcName}-change`}
              onClick={handleOpen}
            >
              Change
            </TextButton>
          </div>
        }
      />
      <Dialog
        title="Comp Tags Options"
        dataHcName={`${dataHcName}-dialog`}
        active={active}
        onClose={handleClose}
        actions={[
          {
            secondary: true,
            dataHcName: `${dataHcName}-cancel`,
            label: 'Cancel',
            onClick: () => {
              setTagOptions(savedOptions || []);
              setNewTag('');
              handleClose();
            },
          },
          {
            dataHcName: `${dataHcName}-save`,
            label: 'Save',
            onClick: () => {
              if (tagOptions.length) {
                patchReportPreferences([
                  {
                    op: 'add',
                    path: `/tagOptions`,
                    value: tagOptions,
                  },
                ]);
              } else {
                patchReportPreferences([
                  {
                    op: 'remove',
                    path: `/tagOptions`,
                  },
                ]);
              }
              handleClose();
            },
          },
        ]}
      >
        {hasCustomOptions ? (
          <>
            <h3>Your Custom Tags:</h3>
            <TwoColumnTable
              compact
              borders
              dataHcName={`${dataHcName}-custom`}
              rows={tagOptions.map((t) => ({
                label: t,
                value: (
                  <IconButton
                    dataHcName={`${dataHcName}-delete`}
                    onClick={() => {
                      setTagOptions(tagOptions.filter((o) => t !== o));
                    }}
                    icon={<DeleteIcon />}
                  />
                ),
              }))}
            />
          </>
        ) : (
          <>
            <h3>Using Default Tags:</h3>
            <TwoColumnTable
              compact
              borders
              dataHcName={`${dataHcName}-default`}
              rows={DEFAULT_TAGS.map((t) => ({
                label: t,
                value: null,
              }))}
            />
          </>
        )}
        <div className={styles.AddTagRow}>
          <Input
            dataHcName={`${dataHcName}-new`}
            ref={inputRef}
            className={styles.NewTagInput}
            placeholder="Add New Tag"
            onChange={setNewTag}
            value={newTag}
            onKeyUp={(e) => e.key === 'Enter' && handleAddTag()}
          />
          <Button
            dataHcName={`${dataHcName}-add`}
            label="Add"
            disabled={!newTag || !!tagOptions.find((t) => t === newTag)}
            onClick={handleAddTag}
          />
        </div>
      </Dialog>
    </>
  );
};
